/**
 * Workspace shell — unified Parcel entry.
 *
 * Boot sequence (mirrors RUNNER_ARCHITECTURE.md §Boot sequence):
 *  1. Early lang/theme from localStorage (synchronous, first-paint)
 *  2. bootShell: polyglot init → ensureSDKInit → registerSdkAdapter
 *     → auth gate → eager USER/ORG/WORKSPACE/WP fetch → mount shell
 *  3. dispatchUrl: initial route → lazy-load sub-app or shared page into Slot
 *  4. wireGlobalListeners: popstate + cross-tab storage events
 *
 * Phase 8: app manifests imported from each sub-app package.
 * Each manifest declares key, routes, i18nNamespaces, app/context lazy
 * imports, and optional fetchOnMount / onParamsChange lifecycle hooks.
 */

// WHITELABEL-1 layer 4 (tickets/opus.md) — MUST stay the very first import
// in this file. It forces MWT off (no `/w/<org>` prefix, ever) on a
// host-resolved (custom-domain) boot, before anything else in the boot
// graph can read+cache the MWT flag. See rootMappedBoot.js's own header for
// the full ordering rationale — do not reorder this above the imports it
// currently sits above; it must stay above them.
import './rootMappedBoot.js'

// Slot 2 by contract — pins the dev API channel (url > SYMBOLS_API_CHANNEL >
// stored > 'development') before the workspace-shared graph below can
// memoize @symbo.ls/channels. See channelBootstrap.js for the full story;
// rootMappedBoot stays above it (whitelabel MWT ordering, and it never
// reads channels).
import './channelBootstrap.js'

import _wsShared from '@symbo-ls/workspace-shared'
const bootShell = (...args) => _wsShared.functions.bootShell(...args)
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)
const workspaceApiBaseForBrand = (...args) => _wsShared.functions.workspaceApiBase(...args)
const shellGetActiveWorkspacePointer = (...args) =>
  _wsShared.functions.getActiveWorkspacePointer(...args)
const shellSetActiveWorkspaceRecord = (...args) =>
  _wsShared.functions.setActiveWorkspaceRecord(...args)
const shellGetWorkspaceRawSegments = (...args) =>
  _wsShared.functions.getWorkspaceRawSegments(...args)
import { wireGlobalListeners } from './wireGlobalListeners.js'
import { armAuthShellModalHost } from './armAuthShellModalHost.js'
import {
  redirectIfUnauthed,
  hasSessionToken,
  armLoginBounceClearOnGesture
} from './functions/auth.js'
import { isPublicShareViewPath } from './functions/shareLinks.js'
import { primePeople } from './functions/peoplePrime.js'
import { getWorkspaceCached, primeWorkspaceCache } from './functions/getWorkspaceCached.js'
import { convergeBootWorkspace } from './functions/convergeBootWorkspace.js'
import {
  bootModulePrefetchAllowed,
  primeAndPrefetchModule
} from './functions/bootModulePrefetch.js'
import { prefetchWorkspaceModule } from './functions/workspaceProjects.js'
import {
  resolveWarmBootSnapshot,
  applyWarmBootSkin,
  readSessionBootOrgs
} from './functions/bootSnapshot.js'
import { withBootLangHint } from './functions/bootLang.js'
import {
  kickWhitelabelBrand,
  seedWhitelabelBrand,
  seedWhitelabelHostScope,
  applyWhitelabelBrandLate
} from './functions/whitelabelBrand.js'
import { isCustomDomainHost } from './functions/customDomainHost.js'
import context from './context.js'

import canvasManifest from '../canvas/manifest.js'
import { awaitBridgeVerdict } from '../canvas/bootBridgeGate.js'
import wpManifest from './manifest.js'
import marketplaceManifest from '../marketplace/manifest.js'
import previewManifest from '../preview/manifest.js'
// /analytics (project) + /workspace-analytics (+ /workspace-analytics/map
// + /workspace-analytics/logs) are owned by the
// @symbo-ls/workspace-analytics package — consumed as a SHARED LIBRARY
// (registered in sharedLibraries.js) so its components, functions, and
// state register globally in the shell scope. Its pages flow through
// workspace/pages/index.js, which imports them directly from the
// analytics package.

// A comm host installs as its own app (comm.webmanifest: its name, start_url
// /chat). The browser reads the manifest link lazily (install, app menu), so
// pointing it here at module load is in time. The `new URL` is also what makes
// the build emit the file beside app.webmanifest.
if (_wsShared.functions.isCommHost()) {
  const manifestLink = document.querySelector('link[rel="manifest"]')
  if (manifestLink) manifestLink.href = new URL('./comm.webmanifest', import.meta.url).href
}

// Run the bidirectional auth-route gate at shell entry — BEFORE
// bootShell, sub-app manifests, or any page component load. This
// guarantees the redirect fires for every route, not just routes that
// activate the workspace-project sub-app. The wp sub-app's own
// `app.js` also calls `redirectIfUnauthed` (kept as defense-in-depth),
// but routes like /reset-password / /signup-confirmation are handled
// by shared pages that mount directly via the unified pages map
// without ever importing wp/app.js — so this top-level call is what
// catches them.
//
// `publicPathPrefixes` matches the wp sub-app's allowlist so guest-
// reachable surfaces (/meet-guest, /_session-bridge, /user/:slug,
// /u/:slug) keep rendering for both authed and unauthed visitors.
// Capture whether the gate issued a hard redirect. When it did, the page is
// already navigating away (unauthed → /demo on the demo host, → /login on
// my.symbols.app; or authed-on-/demo → /), so we must NOT run the boot below:
// bootShell would burn a full SDK-init + getMe round-trip on the doomed page AND
// fire its own soft `replaceState('/login')`, which races the hard redirect —
// the demo's "/ → flash /login → /demo" multi-bounce. Skipping it makes the
// bounce a single clean redirect.
// The public paths and the decision are functions/auth.js's
// THE public-path list (shared guestRoutes.js isGuestAllowedPath, the boot
// gate's own) + authRedirectTarget — the SAME predicate the
// router's authRouteGuard runs on every navigation (and the published shell's
// only gate, since mermaid never runs this file).
// A comm host (docs/COMM_HOST.md) borrows the session of its workspace host
// through the session bridge — and the bridge answers AFTER this gate used to
// decide, so a person signed in on my.symbols.app was bounced to
// comm.symbols.app/login. With no session of its own, a comm host's protected
// path waits for the bridge's verdict first (the canvas boot's bounded
// awaitBridgeVerdict) and runs this same gate after it, in the boot below.
const _commBridgeWait =
  _wsShared.functions.isCommHost() &&
  !hasSessionToken() &&
  !_wsShared.functions.isGuestAllowedPath(window.location.pathname || '/')
const _redirecting = _commBridgeWait
  ? false
  : redirectIfUnauthed({
      isPublicPath: (p) => _wsShared.functions.isGuestAllowedPath(p)
    })

// Legacy SDKs prime the roster in parallel with auth+mount so members do
// not wait behind getMe → scope → create(). Composite boot already returns
// those rows through context.bootPayload; another prime would fetch and
// retain a duplicate roster on every authenticated load. Missing or failed
// composite sections still fall back to a scoped read in loadPrivateData.
if (!_redirecting && hasSessionToken()) {
  try {
    const sdk = shellGetSDK()
    if (typeof sdk.boot !== 'function') primePeople(sdk)
  } catch (err) {
    console.warn('[workspace] roster prime skipped', err?.message || err)
  }
  // Legacy boot shares this early workspace request with bootShell's scope
  // fetch and loadWorkspaceProject through getWorkspaceCached. Composite
  // boot already returns the full record and bootShell adopts it directly.
  // Missing, failed, or mismatched composite records use bootShell's scoped
  // fallback through the same cache after the client resolves its workspace.
  try {
    const sdk = shellGetSDK()
    if (typeof sdk.boot !== 'function') {
      const _pointerWs = shellGetActiveWorkspacePointer()
      // perf/shell-boot: the same record starts the takeover module's
      // channel + payload read (see the composite hook below).
      if (_pointerWs) {
        void getWorkspaceCached(sdk, _pointerWs)
          .then((rec) => {
            if (
              bootModulePrefetchAllowed({
                wsId: _pointerWs,
                record: rec,
                pointer: shellGetActiveWorkspacePointer(),
                segments: shellGetWorkspaceRawSegments(window.location.pathname),
                orgs: _bootOrgsForPrefetch()
              })
            ) {
              prefetchWorkspaceModule(sdk, _pointerWs, rec)
            }
          })
          .catch(() => {})
      }
    }
  } catch (err) {
    console.warn('[workspace] workspace prime skipped', err?.message || err)
  }
}

// True when this page load IS the public share-link viewer (/s/<token>,
// optionally behind a /w/<org>[/<workspace>] prefix when a signed-in member
// opens their own link from inside the app). Read once, at entry, from the
// URL the browser actually loaded — the router has not run yet.
const _isPublicShareView =
  typeof window !== 'undefined' && isPublicShareViewPath(window.location.pathname)

// perf/shell-boot (2026-10-09): the composite /core/boot answer carries the
// tab's full workspace record — settings.workspaceModule included — long
// before the shell mounts and the workspace turns authoritative. Measured live
// on staging /w/vault/default: boot answered at ~4.0 s, the module's first read
// went out at 9.6 s and its payload at 13.5 s. bootShell primes the record
// cache with every boot row; this primer keeps that contract and starts the
// takeover module's channel + payload read for the ONE row the boot resolves
// for this tab (functions/bootModulePrefetch.js). Fetch only — the takeover
// pass still decides and injects, and joins this flight instead of a new one.
// The session's orgs, to resolve a `/w/<org>` slug (review D4): the
// pre-resolved auth (warm boot), else the root's list, else the SIGNED-IN
// user's warm snapshot under one day old (review Delta 2 N8 — never the
// unkeyed shared org cache). None → no prefetch.
const _bootOrgsForPrefetch = () =>
  context.preResolvedAuth?.orgs ||
  context.state?.orgs ||
  readSessionBootOrgs({ workspaceId: shellGetActiveWorkspacePointer() }) ||
  null
const _primeAndPrefetchModule = primeAndPrefetchModule(primeWorkspaceCache, {
  getPointer: () => shellGetActiveWorkspacePointer(),
  getSegments: () => shellGetWorkspaceRawSegments(window.location.pathname),
  getOrgs: () => _bootOrgsForPrefetch(),
  getSdk: () => shellGetSDK(),
  prefetch: (sdk, wsId, record) => prefetchWorkspaceModule(sdk, wsId, record)
})

// Warm the smbls framework chunk NOW — bootShell dynamic-imports it only
// AFTER its whole network phase (SDK init → getMe → scope fetches), so the
// chunk download + parse/eval sat serially inside the measured ~1.5s silent
// gap before mount (tickets/workspace.md boot ticket #1). Starting it at
// entry overlaps it with the auth round-trips; bootShell's own import()
// then resolves from the module registry instantly.
void import('smbls').catch(() => {})

// White-label branding (WHITELABEL-BRANDED-BOOT-1).
//
// ⚠️ Nika, 2026-08-19: "do not change anything for symbols — this is for
// bellforge workspace gate only." Hence the explicit host gate on all three
// call sites below rather than relying on the resolver's own internal
// no-op: on a platform host this branch is never entered, so the Symbols
// boot has no extra call, no extra await, and no extra microtask.
//
// On a white-label host it is kicked HERE, at entry, for the same reason the
// framework chunk and the roster are: the branded loader and the branded
// login both want it at first paint, and starting it behind SDK-init → getMe
// would guarantee it arrives after. One public, credential-less GET; the
// awaited seed and the late apply below consume THIS one request.
const _isWhitelabelHost = isCustomDomainHost()
if (_isWhitelabelHost) void kickWhitelabelBrand({ apiBase: workspaceApiBaseForBrand() })

// Phase 8: full manifests replace Phase 7 stubs.
// Keyed by the app identifier used in APP_DISPATCH ('app' field).
// (cms/inspect/gazer packages aren't on disk in this branch — re-add the
// imports when those sub-apps land.)
const apps = {
  workspaceProject: wpManifest,
  canvas: canvasManifest,
  marketplace: marketplaceManifest,
  preview: previewManifest
}

// Use the smbls default router — pages are rendered into the 'content'
// element at the shell root by the framework's built-in routing mechanism.
// All routes live in context.pages (merged from every sub-app) and the
// smbls router matches URL → page automatically on initial render and on
// popstate. No customRouterElement or dispatchUrl needed.

;(async () => {
  // A hard redirect is already in flight (see `_redirecting` above) — bail
  // before bootShell so the doomed page doesn't burn an SDK-init + getMe
  // round-trip and bootShell's own soft /login replaceState can't race the
  // redirect. The pending navigation tears this page down momentarily.
  if (_redirecting) return

  // The comm host's bridge wait (see `_commBridgeWait` above): bounded, then
  // the same gate decides from storage — a borrowed token passes, none bounces.
  if (_commBridgeWait) {
    await awaitBridgeVerdict({
      pathname: window.location.pathname,
      isGuestPath: (p) => _wsShared.functions.isGuestAllowedPath(p),
      startHydration: () => {
        shellGetSDK()
        return _wsShared.functions.awaitCrossAppHydration()
      }
    })
    if (redirectIfUnauthed({ isPublicPath: (p) => _wsShared.functions.isGuestAllowedPath(p) }))
      return
  }

  // WHITELABEL-STRICT-TENANT-SCOPE-1 — bind this tab to the host's tenant
  // FIRST, before anything reads a scope. The seed stamps the tab-side
  // workspace pointer, and the warm-start read just below is keyed by that
  // pointer: resolving it before the seed handed a white-label tab the cached
  // chrome (and member roster) of whatever workspace the account last used on
  // this origin. Awaited, not raced: see seedWhitelabelHostScope. Same single
  // request the brand seed below consumes. Platform hosts skip it entirely.
  if (_isWhitelabelHost) {
    await seedWhitelabelHostScope(context.state, { apiBase: workspaceApiBaseForBrand() })
  }

  // Warm-start (tickets/opus.md "boot: the scope-keyed warm-start snapshot's
  // PAINT half was never built"). Resolved HERE, at the entry — before
  // bootShell's own network phase starts — and passed straight through as
  // an already-validated row; bootShell does no scope judgment of its own
  // (see bootSnapshot.js's resolveWarmBootSnapshot for the full isolation +
  // URL-scope-match contract, and bootShell.js's warm-start block for what
  // it does with a valid one). Null on every cold boot / first-ever visit /
  // just-switched tenant — those fall through to today's behavior unchanged.
  // A white-label host whose tenant could not be established (`false`) gets
  // no warm row at all: the row is keyed by a pointer the host never
  // confirmed, so it may be another tenant's.
  const warmSnapshot =
    context.state.whitelabelHostScope === false ? null : resolveWarmBootSnapshot()
  // THE SKIN BEFORE FIRST PAINT (DESIGN-WL-SKINS-AMBIENT-DEFAULT-SOLID-
  // WHITELABEL-1): the warm row carries the skin the shell stamped for this
  // exact scope, so <html data-skin> is right before the shell's first frame
  // — the header pills and the composer are drawn in their skin, never eased
  // into it when the record lands. A cold boot (no row) leaves the attribute
  // to the shell's own stamp, as before.
  applyWarmBootSkin(warmSnapshot)

  // Seed the brand onto `context.state` BEFORE the mount so a white-label
  // visitor's FIRST paint is already branded (the loader and, on the auth
  // bounce, the login both read `s.root.whitelabelBrand`). Bounded — see
  // whitelabelBrand.js: a slow lookup must never delay the mount, so this
  // resolves null on timeout and `applyWhitelabelBrandLate` (below, on
  // whichever root actually mounts) swaps the brand in when it lands.
  // Platform hosts skip the await entirely (see the gate at entry).
  if (_isWhitelabelHost) {
    // The same single request the seed races below, handed to boot as a
    // promise: step 7b (interface language) awaits it — bounded — when the
    // seed lost the race, so a branded host's `branding.lang` still reaches
    // the first language resolution instead of only the late re-render.
    context.state.whitelabelBrandLookup = kickWhitelabelBrand({
      apiBase: workspaceApiBaseForBrand()
    })
    await seedWhitelabelBrand(context.state, { apiBase: workspaceApiBaseForBrand() })
  }

  const shell = await bootShell({
    // the ONE boot sequence's steps (shared bootPrepare / bootResolve /
    // bootApply), reached through the registry — see bootShell.js
    steps: _wsShared.functions,
    apps,
    defaultApp: 'workspaceProject',
    fallbackRoute: '/',
    context,
    // /s/<token> — the public share-link viewer (PUBLIC-SHARE-LINKS-1).
    // `redirectIfUnauthed` above already lets this path through, but
    // bootShell runs a SECOND gate of its own (the canonical guest-route
    // regex in shared/functions/guestRoutes.js) and would replaceState an
    // anonymous visitor to /login — the exact "guest link asks for signup"
    // failure meet-guest hit in 2026-06. `publicSurface` is that gate's
    // documented opt-out (the preview app uses it for the same reason:
    // a visibility-gated public surface that must render logged-out), and
    // it is set by the CALLER — so the shell opts itself in here instead
    // of the share feature editing the shared allowlist it does not own.
    // Every other route is unaffected: the flag is false unless the entry
    // URL is a share link.
    publicSurface: _isPublicShareView,
    // #17: share the getWorkspace in-flight-dedup + TTL cache with
    // loadWorkspaceProject/onWorkspaceChange so the eager boot scope-fetch and the
    // module-load fetch collapse to one round-trip (was a duplicate getWorkspace).
    getWorkspaceCached,
    // the boot's full-projection rows seed that cache: zero record reads on a
    // cold load (shared bootShell.bootListServesActiveOrg.test.js) — and the
    // tab's own row starts its takeover module (perf/shell-boot, above)
    primeWorkspaceCache: _primeAndPrefetchModule,
    // The warm row, with the language the loader painted from for THIS
    // arrival's scope (`loaderLang`): shared bootPrepare paints a row that
    // carries no language pair in it, as the loader did (functions/bootLang.js
    // withBootLangHint). A row's own pair still wins; no row stays null.
    warmSnapshot: withBootLangHint(warmSnapshot)
  })
  // Boot segment G (BOOT-8S.md): bootShell resolved (its scope fetch, the
  // shell create and the first route are behind this point).
  try {
    performance.mark('wsboot:shell:returned')
  } catch (_) {
    /* no Performance API */
  }

  // THE BOOT DEADLINE (bootDeadline.js): the session check never answered.
  // The loader already shows its error line + reload button and the stored
  // session is kept — mount nothing (no login shell over a valid session).
  if (!shell && context.bootFailed) return

  if (!shell) {
    // Auth gate bounced — bootShell already set URL to /login (soft
    // replaceState) and returned null. SDK init + adapter registration +
    // `context.methods.sdk` assignment + `context.state.notifications`
    // seeding ALL ran inside bootShell before it returned, so this block
    // just needs to mount the shell. The smbls router fires
    // onRenderRouter during create() and auto-routes to /login (which is
    // in context.pages via wpPages), so no manual dispatchUrl call is
    // needed.
    const { create } = await import('smbls')
    const authShell = await create(context.app, {
      ...context,
      globalTheme: context.state?.themeMode || 'auto',
      themeStorageKey: 'themeMode'
    })
    wireGlobalListeners(authShell, { apps, fallbackRoute: '/login' })
    // bootApply never runs on this path, so it never arms the deferred modal
    // host — and sign-up / sign-in continue in THIS shell. Without the arm,
    // every confirm dialog after sign-up opened onto an unmounted host.
    armAuthShellModalHost(authShell)
    // The branded LOGIN is this path: a logged-out white-label visitor.
    // If the brand lost the pre-mount race it lands here instead — the
    // login's own factories re-render on the reactive write.
    if (_isWhitelabelHost) {
      void applyWhitelabelBrandLate(authShell?.state?.root || authShell?.state, {
        apiBase: workspaceApiBaseForBrand()
      })
    }
    return
  }

  // A warm boot's first auth tick leaves the snapshot's list placeholder to
  // bootShell's reconcile (functions/auth.js); converge it here if that
  // reconcile resolved nothing. No request when the record is already full.
  try {
    convergeBootWorkspace(shell.state, {
      sdk: shellGetSDK(),
      getWorkspaceCached,
      setActiveWorkspaceRecord: shellSetActiveWorkspaceRecord
    })
  } catch (err) {
    console.warn('[workspace] post-boot workspace converge skipped', err?.message || err)
  }

  // An AUTHED shell booted — ARM the login-bounce breaker reset on the
  // first trusted user gesture (functions/auth.js). Clearing directly here
  // reset the counter on every lap of any loop whose cycle contains a
  // successful boot (boot OK → post-boot guard bounces back to /login), so
  // the breaker never tripped on exactly those loops and the tab froze
  // (Nika 2026-09-02, /login?next=/w/gita/… report). A machine loop never
  // produces a trusted pointerdown/keydown; a human does within seconds.
  armLoginBounceClearOnGesture()

  // Same second chance for the signed-in path — the branded LOADER is still
  // on screen while boot finishes, so a brand arriving now still shows.
  if (_isWhitelabelHost) {
    void applyWhitelabelBrandLate(shell?.state?.root || shell?.state, {
      apiBase: workspaceApiBaseForBrand()
    })
  }

  wireGlobalListeners(shell, { apps, fallbackRoute: '/' })
})()

// Dev only (Parcel strips `module.hot` from production builds). Parcel's HMR
// client marks an update "handled" without reloading when the changed module
// has not executed yet or lives in a bundle this page has not loaded — the
// common case here, since sub-apps and pages are lazy-loaded. Nothing in the
// shell hot-applies modules, so always fall back to a full reload.
if (module.hot) window.addEventListener('parcelhmraccept', () => location.reload())
